import { useEffect, useState } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  clearCached,
  getCached,
  isStale,
  LIST_CACHE_TTL_MS,
  setCached,
  statutesListCacheKey,
} from '../lib/listCache';

export interface Statute {
  id: string;
  title: string;
  description: string;
  /** Optional; card footer defaults to Pakistan */
  jurisdiction?: string;
  category?: string;
  content: string;
  contentStoredInFile?: boolean;
  storagePath?: string;
  fileUrl?: string;
  pdfUrl?: string;
  documentUrl?: string;
  /** Optional reader chrome: Section vs Article labels in TOC */
  readerUnitLabel?: 'Section' | 'Article';
  /** Optional nested TOC layout (e.g. QSO-style chapter/heading stacks) */
  readerTocVariant?: 'default' | 'qso';
}

let loadPromise: Promise<Statute[]> | null = null;

async function fetchStatutesFromFirestore(): Promise<Statute[]> {
  const q = query(collection(db, 'statutes'), orderBy('title', 'asc'), limit(100));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Statute[];
}

export function useStatutes() {
  const cacheKey = statutesListCacheKey();
  const cachedStatutes = getCached<Statute[]>(cacheKey);
  const [statutes, setStatutes] = useState<Statute[]>(cachedStatutes ?? []);
  const [loading, setLoading] = useState(cachedStatutes === undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cached = getCached<Statute[]>(cacheKey);
    if (cached) {
      setStatutes(cached);
      setLoading(false);
      if (!isStale(cacheKey, LIST_CACHE_TTL_MS)) {
        return;
      }

      let cancelled = false;
      (async () => {
        try {
          const data = await fetchStatutesFromFirestore();
          setCached(cacheKey, data);
          if (!cancelled) {
            setStatutes(data);
            setError(null);
          }
        } catch (err: unknown) {
          if (cancelled) return;
          console.error('Error revalidating statutes:', err);
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    let cancelled = false;

    const load = async () => {
      try {
        if (!loadPromise) {
          loadPromise = fetchStatutesFromFirestore();
        }
        const data = await loadPromise;
        setCached(cacheKey, data);
        if (!cancelled) {
          setStatutes(data);
          setError(null);
        }
      } catch (err: unknown) {
        loadPromise = null;
        if (cancelled) return;
        console.error('Error fetching statutes from Firestore:', err);
        const message = err instanceof Error ? err.message : 'Failed to fetch statutes';
        setError(message);
        setStatutes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [cacheKey]);

  return { statutes, loading, error };
}

/** Call after admin uploads/edits so the next visit refetches. */
export function invalidateStatutesCache() {
  clearCached(statutesListCacheKey());
  loadPromise = null;
}
